import { ConfirmationMethodCardProps } from './props';
import styles from './styles.module.scss';
import ConfirmationMethodCardPropsIndicator from './Indicator';
import ConfirmationMethodCardSkeleton from './Skeleton';
import { memo } from 'react';

/**
 * ConfirmationMethodCard component that displays a selectable card for confirmation methods
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} props.label - Label text to display in the card
 * @param {bigint} props.value - Value associated with this confirmation method
 * @param {(value: bigint) => void} props.onClick - Callback function when card is clicked
 * @param {boolean} props.selected - Whether this card is currently selected
 * @param {boolean} props.isLoading - Whether the card is in loading state
 * @returns {JSX.Element} Card component with label and selection indicator
 */
function ConfirmationMethodCard({
  label,
  value,
  onClick,
  selected,
  isLoading,
}: ConfirmationMethodCardProps) {
  /**
   * Handles click events on the card
   * Calls the onClick callback with the card's value if provided
   */
  function handleClick() {
    if (onClick) {
      onClick(value);
    }
  }

  // Show skeleton loader while loading
  if (isLoading) {
    return <ConfirmationMethodCardSkeleton />;
  }

  return (
    <div
      onClick={handleClick}
      className={`${styles.card} ${selected ? styles['card--selected'] : ''}`}
    >
      <div className={styles.card__row}>
        <div className={styles.card__column}>
          <h3 className={styles.card__column__label}>{label}</h3>
        </div>
        <ConfirmationMethodCardPropsIndicator selected={selected} />
      </div>
    </div>
  );
}

export default memo(ConfirmationMethodCard);
